import { useState } from "react";
import { useNavigate } from "react-router-dom";
import FileAttachment from "./shared/FileAttachment";
import { useImpactedModules } from "../../hooks/useImpactedModules";
import styles from "../../styles/forms/SharedForm.module.css";
import SplitFormLayout from "../shared/SplitFormLayout";
import { safeLocalStorageGet } from "../../utils/safeJsonParse";
import { getSubmitterInfo, getDefaultCountry } from "../../utils/formHelpers";

import { useFormState } from "../../hooks/useFormState";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import {
  FormSection,
  FormField,
  PinnedSubmit,
  SuccessCard,
} from "./shared/FormComponents";
import { FormProvider } from "./shared/FormContext";
import T, { useTranslate } from "../T";

const STEPS = [
  { id: "reporter", label: "Reporter Info", desc: "Contact & Job Role" },
  { id: "request", label: "Request Details", desc: "Type & Description" },
  { id: "manager", label: "Approval info", desc: "Manager details" },
  { id: "platforms", label: "Platforms", desc: "Access & Mailing lists" },
  { id: "attachments", label: "Attachments", desc: "Relevant files" },
];

const REQUEST_TYPES = [
  "New Employee Onboarding",
  "Off-boarding",
  "Current Employee",
];
const ACCESS_LEVELS = ["Admin", "Read-only(View)", "Editor"];
const COUNTRIES = [
  "Nigeria",
  "Ghana",
  "Kenya",
  "Uganda",
  "Cote d'Ivoire",
  "Senegal",
  "South Africa",
  "Morocco",
  "Egypt",
];

const INITIAL = {
  summary: "",
  description: "",
  contact: "",
  email: "",
  phone: "",
  jobRole: "",
  lineManagerName: "",
  lineManagerEmail: "",
  requestType: "",
  alternateEmail: "",
  country: "",
  platform: "",
  accessLevel: "",
  mailingList: "",
  attachments: [],
};



export default function AccessRequestForm({ onBack }) {
  const { modules: impactedModules, isLoading: modulesLoading } = useImpactedModules();
  const profile = safeLocalStorageGet("staff_profile");
  const defaultCountry = getDefaultCountry(profile);
  const { tText } = useTranslate();

  const {
    formData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    setAllTouched,
    contextValue,
  } = useFormState({
    ...INITIAL,
    contact: profile.firstname
      ? `${profile.firstname} ${profile.lastname}`
      : profile.name || "",
    email: profile.primaryEmail || profile.email || "",
    country: defaultCountry,
  });

  const { submitForm, isSubmitting, submitted, error } = useFormSubmit();
  const navigate = useNavigate();

  const handleBack = onBack || (() => navigate("/staff/dashboard"));

  const onSubmit = () => {
    const profile = safeLocalStorageGet("staff_profile");
    const { submittedBy: profileEmail, creatorName: profileCreatorName } = getSubmitterInfo(profile);
    const submittedBy = formData.email || profileEmail;
    const creatorName = formData.contact || profileCreatorName;

    submitForm(formData, (attachments) => {
      const nocobasePayload = {
        user_name: formData.contact,
        category: 1,
        Summary: formData.summary,
        description: formData.description,
        impactedsystem: formData.platform ? [formData.platform] : [],
        priority: "Medium",
        environment: "Production",
        loanid: "",
        carid: "",
        dealerid: "",
        Description: `Request Type: ${formData.requestType}\n\n${formData.description}\n\nJob Role: ${formData.jobRole}\nPhone: ${formData.phone || "N/A"}\nAlternate Email: ${formData.alternateEmail || "N/A"}`,
        Country: formData.country,
        sourceofrequest: "Staff Portal",
        confluencelink: "",
        accesstype: formData.requestType,
        accesslevel: formData.accessLevel,
        linemanagername: formData.lineManagerName,
        linemanageremail: formData.lineManagerEmail,
        mailinglist: formData.mailingList || "None",
        status: "Open",
        tickettype: "Access Request",
        Resolution: "",
        rootcause: "",
        f_7ygfivdcnao: "",
        Creator: creatorName,
        Creatoremail: submittedBy,
        attachement: attachments.map(a => a.id)
      };

      return { nocobasePayload };
    });
  };

  if (submitted) {
    return (
      <SplitFormLayout
        badge="Internal Support"
        title="Access Request"
        subtitle="Manage system access, onboarding, and off-boarding requests."
        steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
        currentStep={STEPS.length}
        currentBreadcrumb="Request Access"
      >
        <SuccessCard
          title="Request Submitted"
          description="Your access request has been submitted. Your line manager will be notified for approval where required."
          onBack={handleBack}
        />
      </SplitFormLayout>
    );
  }

  const formValid =
    formData.contact &&
    formData.email &&
    formData.jobRole &&
    formData.country &&
    formData.summary &&
    formData.description &&
    formData.requestType &&
    formData.lineManagerName &&
    formData.lineManagerEmail &&
    formData.platform &&
    formData.accessLevel;

  return (
    <SplitFormLayout
      badge="Internal Support"
      title="Access Request"
      subtitle="Manage system access, onboarding, and off-boarding requests."
      steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
      currentBreadcrumb="Request Access"
    >
      <FormProvider value={contextValue}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className={styles.ticketFormWrapper}
      >
        {error && <div className={styles.errorBanner}>{tText(error)}</div>}

        <FormSection id="reporter" title="1. Reporter Information">
          <div className={styles.row2}>
            <FormField label="Contact Name" name="contact" required>
              <input
                type="text"
                name="contact"
                value={formData.contact}
                onChange={handleChange}
                className={styles.input}
                placeholder={tText("Full Name")}
                required
              />
            </FormField>
            <FormField label="E-mail" name="email" required>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={styles.input}
                placeholder="work@autochek.africa"
                required
              />
            </FormField>
          </div>
          <div className={styles.row2} style={{ marginTop: "20px" }}>
            <FormField label="Phone Number" name="phone">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={styles.input}
                placeholder="+234..."
              />
            </FormField>
            <FormField label="Country" name="country" required>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{tText("Select...")}</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{tText(c)}</option>
                ))}
              </select>
            </FormField>
          </div>
          <FormField label="Job / Role Function" name="jobRole" required style={{ marginTop: "20px" }}>
            <input
              type="text"
              name="jobRole"
              value={formData.jobRole}
              onChange={handleChange}
              className={styles.input}
              placeholder={tText("e.g. Software Engineer")}
              required
            />
          </FormField>
        </FormSection>

        <FormSection id="request" title="2. Request Details">
          <FormField label="Access Request Type" name="requestType" required style={{ marginBottom: "20px" }}>
            <select
              name="requestType"
              value={formData.requestType}
              onChange={handleChange}
              className={styles.select}
              required
            >
              <option value="">{tText("Select...")}</option>
              {REQUEST_TYPES.map((t) => (
                <option key={t} value={t}>{tText(t)}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Summary" name="summary" required style={{ marginBottom: "20px" }}>
            <input
              type="text"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              className={styles.input}
              placeholder={tText("Brief summary of your request")}
              required
            />
          </FormField>
          <FormField label="Description" name="description" required>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              rows={4}
              placeholder={tText("Detailed explanation...")}
              required
            />
          </FormField>
        </FormSection>

        <FormSection id="manager" title="3. Managerial Information">
          <div className={styles.row2} style={{ marginBottom: "20px" }}>
            <FormField label="Line Manager Name" name="lineManagerName" required>
              <input
                type="text"
                name="lineManagerName"
                value={formData.lineManagerName}
                onChange={handleChange}
                className={styles.input}
                placeholder={tText("Manager's Full Name")}
                required
              />
            </FormField>
            <FormField label="Line Manager E-mail" name="lineManagerEmail" required>
              <input
                type="email"
                name="lineManagerEmail"
                value={formData.lineManagerEmail}
                onChange={handleChange}
                className={styles.input}
                placeholder="manager@autochek.africa"
                required
              />
            </FormField>
          </div>
          <FormField label="Alternate E-mail" name="alternateEmail">
            <input
              type="email"
              name="alternateEmail"
              value={formData.alternateEmail}
              onChange={handleChange}
              className={styles.input}
              placeholder={tText("Optional secondary email")}
            />
          </FormField>
        </FormSection>

        <FormSection id="platforms" title="4. Platform & Access">
          <div className={styles.row2} style={{ marginBottom: "20px" }}>
            <FormField label="Platform(s)" name="platform" required>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{modulesLoading ? tText("Loading...") : tText("Select...")}</option>
                {impactedModules.map((m) => (
                  <option key={m} value={m}>{tText(m)}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Access Level" name="accessLevel" required>
              <select
                name="accessLevel"
                value={formData.accessLevel}
                onChange={handleChange}
                className={styles.select}
                required
              >
                <option value="">{tText("Select...")}</option>
                {ACCESS_LEVELS.map((l) => (
                  <option key={l} value={l}>{tText(l)}</option>
                ))}
              </select>
            </FormField>
          </div>
          <FormField label="Mailing List" name="mailingList">
            <input
              type="text"
              name="mailingList"
              value={formData.mailingList}
              onChange={handleChange}
              className={styles.input}
              placeholder={tText("e.g. tech-team@autochek.africa")}
            />
          </FormField>
        </FormSection>

        <FormSection id="attachments" title="5. Attachments">
          <FileAttachment
            attachments={formData.attachments}
            onChange={handleAddFiles}
            onRemove={handleRemoveFile}
          />
        </FormSection>

        <PinnedSubmit
          isSubmitting={isSubmitting}
          isValid={formValid}
          onSubmit={onSubmit}
          onInvalidSubmit={setAllTouched}
        />
        
      </form>
      </FormProvider>
    </SplitFormLayout>
  );
}
